import { displayFrtPp } from "@/lib/surchargeDisplay";
import type {
  ApiPortInfo,
  BookingPolicies,
  DndPolicy,
  OverweightPolicy,
  RateCardSummary,
  RateLineDdPolicy,
  RateLineItem,
  WeightLimitPolicy,
} from "@/types/api";
import type { PortInfo } from "@/types/booking";
import {
  classifyRateSource,
  normalizeRateStatus,
  type RateCardPoliciesSnapshot,
  type RateCardRow,
  type RateLineRow,
} from "@/types/rate";

function toPortInfo(port: ApiPortInfo): PortInfo {
  return { locode: port.locode, name: port.name ?? port.locode };
}

function mapApiSurcharge(s: RateLineItem["surcharges"][number]) {
  return {
    code: s.code,
    amount: s.amount,
    currency: s.currency,
    basis: s.basis,
    category: s.category,
  };
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function asBookingPolicies(value: unknown): BookingPolicies | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as BookingPolicies;
  if (
    raw.max_containers == null &&
    raw.min_containers == null &&
    raw.min_teu == null
  ) {
    return null;
  }
  return {
    max_containers: raw.max_containers ?? null,
    min_containers: raw.min_containers ?? null,
    min_teu: raw.min_teu ?? null,
  };
}

/** Card-level policy fields used by the rate-detail rail. */
export function cardPoliciesFromRow(
  card: Pick<
    RateCardRow,
    | "overweight_policies"
    | "weight_limit_policies"
    | "booking_policies"
    | "dnd_policies"
  >,
): RateCardPoliciesSnapshot {
  return {
    overweight_policies: card.overweight_policies,
    weight_limit_policies: card.weight_limit_policies,
    booking_policies: card.booking_policies,
    dnd_policies: card.dnd_policies,
  };
}

/** Lift card-level policy fields off a summary/detail payload. */
export function cardPoliciesFromSummary(
  card: RateCardSummary,
): RateCardPoliciesSnapshot {
  return {
    overweight_policies: asArray<OverweightPolicy>(card.overweight_policies),
    weight_limit_policies: asArray<WeightLimitPolicy>(
      card.weight_limit_policies,
    ),
    booking_policies: asBookingPolicies(card.booking_policies),
    dnd_policies: asArray<DndPolicy>(card.dnd_policies),
  };
}

/**
 * Adapt an API rate card summary into the contract-level row view model the v2
 * rates list and detail hero consume.
 */
export function apiRateCardToRow(card: RateCardSummary): RateCardRow {
  return {
    id: card.id,
    carrier: card.carrier,
    contract_ref: card.contract_ref,
    contract_revision: card.contract_revision,
    named_account: card.named_account,
    source: card.source,
    source_type: classifyRateSource(card.source, card.is_spot),
    status: normalizeRateStatus(card.status),
    valid_from: card.valid_from,
    valid_to: card.valid_to,
    line_count: card.line_count,
    avg_frt: card.avg_frt,
    avg_price_net: card.avg_price_net ?? null,
    is_spot: card.is_spot,
    email_date: card.email_date ?? null,
    created_at: card.created_at,
    batch_id: card.batch_id ?? null,
    batch_attachment_id: card.batch_attachment_id ?? null,
    provenance: card.provenance ?? null,
    ...cardPoliciesFromSummary(card),
  };
}

/** Adapt an API rate line into the line-row view model for the detail table. */
export function apiRateLineToRow(line: RateLineItem): RateLineRow {
  const size = line.equipment_size ?? "";
  const type = line.equipment_type ?? "";
  const summary = [size ? `${size}'` : null, type].filter(Boolean).join(" ");
  return {
    id: line.id,
    rate_card_id: line.rate_card_id,
    carrier: line.carrier,
    contract_ref: line.contract_ref,
    origin_port: toPortInfo(line.origin_port),
    destination_port: toPortInfo(line.destination_port),
    equipment_size: size,
    equipment_type: type,
    equipment_summary: summary || "—",
    is_hazardous: line.is_hazardous,
    currency: line.currency,
    frt: line.frt,
    frt_pp: displayFrtPp(line),
    all_in: line.price_net_overlaid ?? line.price_net ?? line.frt,
    valid_from: line.valid_from,
    valid_to: line.valid_to,
    is_current: line.is_current,
    version: line.version,
    surcharges: (line.surcharges ?? []).map(mapApiSurcharge),
    notice_overlays: line.notice_overlays,
    surcharges_overlaid: line.surcharges_overlaid?.map(mapApiSurcharge) ?? null,
    max_weight: line.max_weight ?? null,
    max_gross_kg: line.max_gross_kg ?? null,
    max_cargo_kg: line.max_cargo_kg ?? null,
    max_weight_basis: line.max_weight_basis ?? null,
    weight_limit_unit: line.weight_limit_unit ?? null,
    free_time_days: line.free_time_days ?? null,
    free_time_days_max: line.free_time_days_max ?? null,
    dd_policies: asArray<RateLineDdPolicy>(line.dd_policies),
    pre_carriage_pol: line.pre_carriage_pol
      ? toPortInfo(line.pre_carriage_pol)
      : null,
    pre_carriage_mot: line.pre_carriage_mot?.trim() || null,
    load_type: line.load_type ?? null,
    freight_basis: line.freight_basis ?? null,
    handling_agent: line.handling_agent ?? null,
    route_via: line.route_via ?? null,
    display_pricing: line.display_pricing ?? null,
  };
}
